import type { SalesReportResponse } from "@gioia365/contracts";
import { useId } from "react";
import { useT } from "../i18n/locale";
import { KpiTable, type KpiRow, type KpiTableGroup } from "./KpiTable";
import { dayShortText } from "./report-format";

// «Giorno per giorno» (piano M7, Task 3; mock-up `daysSection`): le righe del foglio, una per giorno lavorativo e per ogni giorno di
// chiusura in cui qualcuno ha lavorato (li sceglie il server), dal più recente, e «Totale del periodo», i cui Lead diversi sono contati
// una volta: più bassi della somma dei giorni. Con un giorno solo («Oggi», «Ieri») la sezione non c'è. Le colonne partono da
// «Chiamate», come nel mock-up; le schede, sotto 860 px, partono chiuse tranne il totale.

type Props = {
  report: SalesReportResponse;
  /** Il periodo scritto, per la didascalia. */
  range: string;
  /** Di chi sono i numeri: «Tutto il team», i nomi scelti o la persona. */
  whose: string;
  /** Il gruppo di colonne scelto, nello stato della pagina. */
  group: KpiTableGroup;
  onGroupChange: (group: KpiTableGroup) => void;
};

export function DaysSection({ report, range, whose, group, onGroupChange }: Props) {
  const t = useT();
  const titleId = useId();
  if (report.days.length < 2) return null;
  const rows: KpiRow[] = report.days
    .slice()
    .reverse()
    .map((day) => ({ key: day.day, label: dayShortText(t, day.day), metrics: day.metrics }));
  rows.push({ key: "total", label: t("reports.days.total"), sub: t("reports.table.countedOnce"), metrics: report.totals.current, total: true });
  return (
    <section className="g-report-section" aria-labelledby={titleId}>
      <KpiTable
        heading={
          <>
            <h2 id={titleId}>{t("reports.days.title")}</h2>
            <p className="g-report-hint">{t("reports.days.intro", { whose })}</p>
          </>
        }
        caption={t("reports.days.caption", { range })}
        firstHead={t("reports.days.firstHead")}
        groupsLabel={t("reports.days.groupsLabel")}
        rows={rows}
        targets={report.targets}
        openCards={false}
        group={group}
        onGroupChange={onGroupChange}
      />
      <p className="g-report-footnote">{t("reports.days.note")}</p>
    </section>
  );
}
